import { ActionIcon, Badge, deepMerge, Text, Tooltip } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { OnChangeFn, RowSelectionState } from '@tanstack/react-table';
import {
  MRT_ColumnDef,
  MRT_Row,
  MRT_RowSelectionState,
  MRT_TableOptions,
} from 'mantine-react-table';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  getPresetColumnVisibility,
  LotsColumnPreset,
} from '../lots-validation/lots-column-preset';
import { lotsApi } from '../../lib/apis/lots';
import { LotListItem } from '../../lib/types/lots';

import {
  formatAreaHa,
  formatTonnage,
  formatYield,
  getAreaDeltaValue,
  getSpeciesName,
  getTonnageDeltaPercentValue,
  getTonnageDeltaValue,
  getYieldDeltaValue,
  isLargeDeltaPercent,
} from './lots-formatters';

import { Permission, usePermissions } from '@auth/data-access';
import { formatDate, useFiltersTableOptions } from '@data-access';
import { PfTable, TsrButtonLink, useRowSelection } from '@ui';

interface LotsValidationTableProps {
  rowSelection: MRT_RowSelectionState;
  setRowSelection: OnChangeFn<RowSelectionState>;
  columnPreset: LotsColumnPreset;
  data: LotListItem[];
  rowCount: number;
  isLoading: boolean;
  isFetching: boolean;
  isPlaceholderData: boolean;
  onValidateSuccess?: () => void;
}

function DeltaCell({
  value,
  percent,
  unit,
  showPercent = false,
}: {
  value: number | null;
  percent?: number | null;
  unit: string;
  showPercent?: boolean;
}) {
  if (value === null || Number.isNaN(value)) {
    return <Text c="dimmed">—</Text>;
  }

  const highlight =
    percent !== undefined && percent !== null
      ? isLargeDeltaPercent(percent)
      : Math.abs(value) >= 1;

  const formatted =
    showPercent && percent != null
      ? `${value > 0 ? '+' : ''}${value.toFixed(2).replace('.', ',')} ${unit} (${percent > 0 ? '+' : ''}${percent.toFixed(1).replace('.', ',')}%)`
      : `${value > 0 ? '+' : ''}${value.toFixed(2).replace('.', ',')} ${unit}`;

  return (
    <Text c={highlight ? 'red.7' : undefined} fw={highlight ? 600 : 400}>
      {formatted}
    </Text>
  );
}

export const LotsValidationTable: FC<LotsValidationTableProps> = ({
  rowSelection,
  setRowSelection,
  columnPreset,
  data,
  rowCount,
  isLoading,
  isFetching,
  isPlaceholderData,
  onValidateSuccess,
}) => {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const canManage = can(Permission.ManageContracts);
  const filtersTableOptions = useFiltersTableOptions<LotListItem>('lots');

  const columnVisibility = useMemo(
    () => getPresetColumnVisibility(columnPreset),
    [columnPreset],
  );

  const validateLot = useMutation({
    mutationFn: lotsApi.validate,
    onSuccess: () => onValidateSuccess?.(),
  });

  const rowSelectionProps = useRowSelection<LotListItem>({
    data,
    rowSelection,
    setRowSelection,
  });

  const columns = useMemo<MRT_ColumnDef<LotListItem>[]>(
    () => [
      {
        accessorKey: 'farm_name',
        header: t('lots.columns.farm_name'),
        enableColumnFilter: false,
        size: 220,
        Cell: ({ cell, row }) => {
          const farmName = cell.getValue<string>();
          const { contract_id: contractId } = row.original;

          if (contractId == null) {
            return (
              <Text span truncate="end">
                {farmName}
              </Text>
            );
          }

          return (
            <TsrButtonLink
              fullWidth
              justify="start"
              maw={220}
              ml="calc(var(--button-padding-x-compact-md) * -1)"
              params={{
                contractId: contractId.toString(),
              }}
              size="compact-md"
              td="underline"
              to="/os/contracts/$contractId/batches"
              tt="capitalize"
              variant="subtle"
            >
              <Text span truncate="end">
                {farmName}
              </Text>
            </TsrButtonLink>
          );
        },
      },
      {
        id: 'species__name',
        accessorFn: (row) => getSpeciesName(row.species),
        header: t('common.crop'),
        enableColumnFilter: false,
        size: 160,
        Cell: ({ row }) => getSpeciesName(row.original.species),
      },
      {
        accessorKey: 'year',
        header: t('lots.columns.year'),
        enableColumnFilter: false,
        size: 90,
      },
      {
        accessorKey: 'is_validated',
        header: t('lots.columns.validation_status'),
        enableColumnFilter: false,
        size: 130,
        Cell: ({ row }) => {
          if (row.original.is_validated) {
            return (
              <Badge color="pf-green" variant="light">
                {t('lots.status.validated')}
              </Badge>
            );
          }
          return (
            <Badge color="orange" variant="light">
              {t('lots.status.pending')}
            </Badge>
          );
        },
      },
      {
        accessorKey: 'validated_at',
        header: t('lots.columns.validated_at'),
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const value = cell.getValue<string | null>();
          if (!value) return '—';
          return formatDate({ date: value, format: 'display' });
        },
      },
      {
        accessorKey: 'oleoze_tonnage',
        header: t('lots.columns.oleoze_tonnage'),
        enableColumnFilter: false,
        Cell: ({ cell }) => formatTonnage(cell.getValue<number>()),
      },
      {
        accessorKey: 'real_tonnage',
        header: t('lots.columns.real_tonnage'),
        enableColumnFilter: false,
        Cell: ({ cell }) => formatTonnage(cell.getValue<number>()),
      },
      {
        accessorKey: 'tonnage_delta',
        header: t('lots.columns.tonnage_delta'),
        enableColumnFilter: false,
        accessorFn: (row) => getTonnageDeltaValue(row),
        Cell: ({ row }) => {
          const absolute = getTonnageDeltaValue(row.original);
          const percent = getTonnageDeltaPercentValue(row.original);
          return (
            <DeltaCell percent={percent} unit="t" value={absolute} />
          );
        },
      },
      {
        accessorKey: 'tonnage_delta_percent',
        header: t('lots.columns.tonnage_delta_percent'),
        enableColumnFilter: false,
        accessorFn: (row) => getTonnageDeltaPercentValue(row),
        Cell: ({ row }) => {
          const percent = getTonnageDeltaPercentValue(row.original);
          if (percent === null) {
            return <Text c="dimmed">—</Text>;
          }
          const highlight = isLargeDeltaPercent(percent);
          return (
            <Text c={highlight ? 'red.7' : undefined} fw={highlight ? 600 : 400}>
              {percent > 0 ? '+' : ''}
              {percent.toFixed(1).replace('.', ',')}%
            </Text>
          );
        },
      },
      {
        accessorKey: 'theoretical_area',
        header: t('lots.columns.theoretical_area'),
        enableColumnFilter: false,
        Cell: ({ cell }) => formatAreaHa(cell.getValue<number>()),
      },
      {
        accessorKey: 'calculated_area',
        header: t('lots.columns.calculated_area'),
        enableColumnFilter: false,
        Cell: ({ cell }) => formatAreaHa(cell.getValue<number | null>()),
      },
      {
        accessorKey: 'area_delta',
        header: t('lots.columns.area_delta'),
        enableColumnFilter: false,
        accessorFn: (row) => getAreaDeltaValue(row),
        Cell: ({ row }) => (
          <DeltaCell unit="ha" value={getAreaDeltaValue(row.original)} />
        ),
      },
      {
        accessorKey: 'real_yield',
        header: t('lots.columns.real_yield'),
        enableColumnFilter: false,
        Cell: ({ row }) => formatYield(row.original.real_yield),
      },
      {
        accessorKey: 'recalculated_yield',
        header: t('lots.columns.recalculated_yield'),
        enableColumnFilter: false,
        Cell: ({ row }) => formatYield(row.original.recalculated_yield),
      },
      {
        accessorKey: 'yield_delta',
        header: t('lots.columns.yield_delta'),
        enableColumnFilter: false,
        accessorFn: (row) => getYieldDeltaValue(row),
        Cell: ({ row }) => (
          <DeltaCell unit="t/ha" value={getYieldDeltaValue(row.original)} />
        ),
      },
      {
        accessorKey: 'agrest_yield',
        header: t('lots.columns.agrest_yield'),
        enableColumnFilter: false,
        Cell: ({ row }) => formatYield(row.original.agrest_yield),
      },
    ],
    [t],
  );

  const tableOptions: MRT_TableOptions<LotListItem> = deepMerge(
    {
      columns,
      data,
      rowCount,
      enableColumnFilters: false,
      enableSorting: true,

      state: {
        isLoading,
        showLoadingOverlay: isFetching && isPlaceholderData,
        rowSelection,
        columnVisibility,
      },

      initialState: {
        showColumnFilters: false,
      },

      mantinePaperProps: {
        h: '100%',
      },

      ...(canManage ? rowSelectionProps : { enableRowSelection: false }),
    },
    filtersTableOptions,
  );

  return (
    <PfTable<LotListItem>
      options={tableOptions}
      renderRowActionsExtension={
        canManage
          ? ({ row }: { row: MRT_Row<LotListItem> }) => {
              if (row.original.is_validated) {
                return null;
              }
              return (
                <Tooltip label={t('lots.validate_lot')}>
                  <ActionIcon
                    color="pf-green"
                    loading={
                      validateLot.isPending &&
                      validateLot.variables === row.original.id
                    }
                    onClick={() => validateLot.mutate(row.original.id)}
                    variant="subtle"
                  >
                    <IconCheck size={18} />
                  </ActionIcon>
                </Tooltip>
              );
            }
          : undefined
      }
    />
  );
};

export default LotsValidationTable;
